import React, { useContext, useState, useEffect } from 'react';
import { auth } from '../firebaseConfig';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { useDispatch, useSelector } from 'react-redux';
import { setAuthState } from '../redux/slices/authSlice';
import { normalizeUserProfile } from '../utils/userDisplay.js';

// Create the auth context
// This context is maintained for backward compatibility
// New components should use Redux directly instead
const AuthContext = React.createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  // Only maintain loading state locally
  const [loading, setLoading] = useState(true);

  // Use Redux for all user state management
  const dispatch = useDispatch();
  const currentUser = useSelector(state => state.auth.currentUser);
  const userData = useSelector(state => state.auth.userData);
  const isAuthenticated = useSelector(state => state.auth.isAuthenticated);
  const authLoading = useSelector(state => state.auth.loading);

  // Watch auth state changes and set up real-time listener for user data
  useEffect(() => {
    let unsubscribeAuth = () => { };
    let unsubscribeUserData = () => { };
    const lowQuota = process.env.REACT_APP_LOW_QUOTA_MODE === 'true';

    const setupAuthListener = async (user) => {
      // Update Redux state with auth user
      if (user) {
        try {
          if (lowQuota) {
            // In low quota mode, avoid reads and just set minimal auth state
            const normalized = normalizeUserProfile({ doc: null, authUser: user, prev: userData });
            dispatch(setAuthState({
              currentUser: {
                uid: user.uid,
                email: user.email,
                emailVerified: user.emailVerified,
                photoURL: user.photoURL || normalized?.photoURL || ''
              },
              userData: normalized
            }));
          } else {
            // Set up real-time listener for user data with metadata changes for cache/server diagnostics
            const userRef = doc(db, 'users', user.uid);
            unsubscribeUserData = onSnapshot(
              userRef,
              { includeMetadataChanges: true },
              (snap) => {
                if (snap.exists()) {
                  const docData = { id: snap.id, ...snap.data() };
                  const normalized = normalizeUserProfile({ doc: docData, authUser: user, prev: userData });
                  dispatch(setAuthState({
                    currentUser: {
                      uid: user.uid,
                      email: user.email,
                      emailVerified: user.emailVerified,
                      photoURL: normalized?.photoURL || user.photoURL || ''
                    },
                    userData: normalized
                  }));
                } else {
                  const normalized = normalizeUserProfile({ doc: null, authUser: user, prev: userData });
                  dispatch(setAuthState({
                    currentUser: {
                      uid: user.uid,
                      email: user.email,
                      emailVerified: user.emailVerified,
                      photoURL: user.photoURL || normalized?.photoURL || ''
                    },
                    userData: normalized
                  }));
                }
              },
              (error) => {
                console.error('Error in user data listener:', error);
                // Implement retry logic here if needed
              }
            );
          }
        } catch (error) {
          console.error('Error setting up user data listener:', error);
          // Implement retry logic here if needed
        }
      } else {
        // Clear auth state when user logs out
        dispatch(setAuthState(null));

        // Ensure unsubscription from user data listener on logout
        if (typeof unsubscribeUserData === 'function') {
          unsubscribeUserData();
        }
      }

      setLoading(false);
    };

    // Set up auth state listener
    unsubscribeAuth = onAuthStateChanged(auth, setupAuthListener);

    // Cleanup function
    return () => {
      unsubscribeAuth();
      if (typeof unsubscribeUserData === 'function') {
        unsubscribeUserData();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  // For backward compatibility, we provide the same context values
  // but they now come from Redux
  const value = {
    currentUser,
    userData,
    isAuthenticated,
    loading: loading || authLoading
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
} 